// ==========================================
// BONIFICAÇÃO DO PARCEIRO COMERCIAL — regra de 26/10/2026
// ==========================================
// Fonte: "Simulador de Bonificação Vigor" do Vinicius Alves (versão de 27/10/2026),
// copiado função por função. A conta tem que bater centavo a centavo com o
// simulador: os testes em __tests__/bonificacaoParceiro.test.ts conferem os
// valores com a versão dele. Mudou a regra lá → muda REGRA_BONIFICACAO aqui e o
// teste aponta o que mexeu.
//
// Arquivo puro de propósito (nada de supabase): é usado pelo simulador do
// Academy, pela Folha e pelas telas de comissão, e testado com node:test.

export const REGRA_BONIFICACAO = {
  tarifa: 1.16, // R$ por kWh da fatura de referência (tarifa travada)
  padrao: 20, // desconto padrão e mínimo (%): até aqui, sem redutor
  meio: 25, // de 21% até aqui, cada ponto tira perda1 pontos da bonificação
  perda1: 2, // pontos tirados por ponto de desconto de 21% a 25%
  perda2: 3, // pontos tirados por ponto de desconto de 26% ao máximo
  maximo: 35, // desconto máximo aceito (%), só número inteiro
  autoriza: 20, // acima deste desconto é condição especial: precisa de aprovação da Vigor
  // um nível por desconto: N1 = 20% (plano padrão) até N16 = 35%
  classes: Array.from({ length: 16 }, (_, i) => [`N${i + 1}`, 20 + i, 20 + i] as const),
  planos: [20], // planos padrão que o parceiro escolhe livremente; o resto é condição especial
  motivos: [
    'Proposta de outra empresa',
    'Consumidor de grande volume',
    'Vários consumidores fechados juntos',
    'Renegociação ou retenção',
    'Outro motivo',
  ],
  entrada: 0.3, // Pix I: 30% da bonificação, no dia 10 do mês seguinte ao aceite
  recBase: 0.02, // Variável: % de cada fatura paga, com o desconto padrão (× redutor)
  recMax: 0.02, // Variável: trava, nunca mais que 2% da fatura paga
  // segmento pelo consumo: até quanto de desconto cada tamanho de consumidor pode ter
  segmentos: [
    [1499, 'C', 25],
    [4999, 'B', 30],
    [Infinity, 'A', 35],
  ] as const,
  minimo: 500, // consumo mínimo por consumidor, em kWh/mês
  indicacao: 50, // valor fixo quando o parceiro só indica e a Vigor vende
  // [até kWh somados no mês, % da fatura com desconto]
  faixas: [
    [2500, 0.2],
    [5000, 0.25],
    [10000, 0.45],
    [20000, 0.6],
    [39999, 0.8],
    [Infinity, 1.2],
  ] as const,
  inicio: [2026, 10] as const, // 1º mês de aceite pela regra nova
  vigencia: '26/10/2026', // vale para consumidores aceitos a partir desta data
  vigenciaIso: '2026-10-26',
  recorrenteAnos: 3, // Variável: o recorrente dura 3 anos depois da assinatura (site, 27/10/2026)
  pagamentoMinimo: 50, // total do mês abaixo disso passa para o mês seguinte
  maxPorMes: 30, // limite de consumidores por mês no simulador
} as const;

const R = REGRA_BONIFICACAO;

export type Modalidade = 'fixa' | 'variavel';

/** Arredonda o centavo como o Excel (meio centavo para cima). */
export function cent(x: number): number {
  return Math.round(Number((x * 100).toPrecision(15))) / 100;
}

/** Pontos da bonificação que o desconto tira (0 no plano padrão). */
export function perdaPts(d: number): number {
  if (d <= R.padrao) return 0;
  if (d <= R.meio) return R.perda1 * (d - R.padrao);
  return R.perda1 * (R.meio - R.padrao) + R.perda2 * (d - R.meio);
}

/** Parte da bonificação que fica com o parceiro (1 = 100% no plano padrão). */
export function redutor(d: number): number {
  return 1 - perdaPts(d) / 100;
}

/** Quanto o parceiro recebe com o desconto d, comparado ao plano padrão (fatura menor × redutor). */
export function vsPadrao(d: number): number {
  return ((100 - d) / (100 - R.padrao)) * redutor(d);
}

/** Variável: % de cada fatura paga (2% × redutor, nunca acima de 2%). Não depende da faixa. */
export function recPct(d: number): number {
  return Math.min(R.recBase * redutor(d), R.recMax);
}

export interface Segmento {
  n: 'A' | 'B' | 'C';
  /** desconto máximo (%) do segmento */
  max: number;
  de: number;
  ate: number;
}

export function segmentoDe(c: number): Segmento | null {
  if (!Number.isFinite(c) || c < R.minimo) return null;
  for (let i = 0; i < R.segmentos.length; i++) {
    const g = R.segmentos[i];
    if (c <= g[0]) return { n: g[1], max: g[2], de: i ? R.segmentos[i - 1][0] + 1 : R.minimo, ate: g[0] };
  }
  return null;
}

export interface Nivel {
  n: string;
  de: number;
  ate: number;
}

export function nivelDe(d: number): Nivel | null {
  for (const k of R.classes) if (d >= k[1] && d <= k[2]) return { n: k[0], de: k[1], ate: k[2] };
  return null;
}

/** Desconto aceito pela regra: inteiro, de 20% a 35% (o teto do segmento é conferido à parte). */
export function descontoValido(d: number): boolean {
  return Number.isFinite(d) && Math.abs(d - Math.round(d)) < 1e-9 && d >= R.padrao && d <= R.maximo;
}

export function ehPlanoPadrao(d: number): boolean {
  return (R.planos as readonly number[]).includes(d);
}

export interface Faixa {
  /** índice: 0 = F1 */
  i: number;
  pct: number;
}

export function faixaDe(v: number): Faixa | null {
  for (let i = 0; i < R.faixas.length; i++) if (v <= R.faixas[i][0]) return { i, pct: R.faixas[i][1] };
  return null;
}

/** De/até (kWh somados no mês) de cada faixa: F1 começa no mínimo por consumidor. */
export function limitesFaixa(i: number): { de: number; ate: number } {
  return { de: i ? R.faixas[i - 1][0] + 1 : R.minimo, ate: R.faixas[i][0] };
}

// ---------- CPF / CNPJ (aceita o CNPJ alfanumérico da Receita) ----------

export function docLimpo(v: string | null | undefined): string {
  return String(v || '').toUpperCase().replace(/[^0-9A-Z]/g, '');
}

export function docMascara(v: string | null | undefined): string {
  let d = docLimpo(v);
  if (d.length <= 11 && /^\d*$/.test(d)) {
    return d
      .replace(/^(\d{3})(\d)/, '$1.$2')
      .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d{1,2})$/, '.$1-$2');
  }
  d = d.slice(0, 14);
  return d
    .replace(/^(\w{2})(\w)/, '$1.$2')
    .replace(/^(\w{2})\.(\w{3})(\w)/, '$1.$2.$3')
    .replace(/^(\w{2})\.(\w{3})\.(\w{3})(\w)/, '$1.$2.$3/$4')
    .replace(/\/(\w{4})(\w{1,2})$/, '/$1-$2');
}

function cpfOk(d: string): boolean {
  if (/^(\d)\1{10}$/.test(d)) return false;
  for (let t = 9; t < 11; t++) {
    let s = 0;
    for (let i = 0; i < t; i++) s += +d[i] * (t + 1 - i);
    if (((s * 10) % 11) % 10 !== +d[t]) return false;
  }
  return true;
}

function cnpjOk(d: string): boolean {
  if (/^(\d)\1{13}$/.test(d)) return false;
  const v = (ch: string) => ch.charCodeAt(0) - 48;
  for (let t = 12; t < 14; t++) {
    let s = 0;
    let p = t - 7;
    for (let i = 0; i < t; i++) {
      s += v(d[i]) * p;
      p = p === 2 ? 9 : p - 1;
    }
    const r = s % 11;
    const dv = r < 2 ? 0 : 11 - r;
    if (dv !== +d[t]) return false;
  }
  return true;
}

/** '' = vazio, 'x' = inválido ou incompleto. */
export function docTipo(v: string | null | undefined): '' | 'CPF' | 'CNPJ' | 'x' {
  const d = docLimpo(v);
  if (!d) return '';
  if (d.length === 11 && /^\d+$/.test(d)) return cpfOk(d) ? 'CPF' : 'x';
  if (d.length === 14 && /^[0-9A-Z]{12}\d{2}$/.test(d)) return cnpjOk(d) ? 'CNPJ' : 'x';
  return 'x';
}

// ---------- cálculo do mês ----------

export interface ConsumidorBonificacao {
  /** consumo médio dos últimos 12 meses, kWh/mês */
  c: number;
  /** desconto do consumidor, em % (20 a 35) */
  d: number;
  /** CPF/CNPJ: o mesmo documento só conta uma vez no mês */
  doc?: string | null;
}

export interface LinhaBonificacao {
  c: number;
  d: number;
  dInt: boolean;
  /** desconto dentro da regra e do teto do segmento */
  dOk: boolean;
  sg: Segmento | null;
  /** desconto acima do teto do segmento */
  dSeg: boolean;
  /** entra na conta do mês */
  ok: boolean;
  dup?: boolean;
  doc: string;
  /** fatura com desconto (R$/mês) */
  F: number;
  /** bonificação cheia, só pela faixa */
  Bb: number;
  /** bonificação total, com o redutor */
  B: number;
  /** quanto o desconto tirou, em R$ */
  perda: number;
  /** Pix I (30%) */
  E: number;
  /** Pix II (70%) */
  S: number;
  /** Variável: recorrente por fatura paga, estimado pela fatura com desconto */
  P: number;
  /** bonificação limitada ao teto de R$ 1,16 por kWh */
  teto: boolean;
}

export interface ResultadoBonificacao {
  linhas: LinhaBonificacao[];
  /** kWh somados dos consumidores que entram */
  V: number;
  fx: Faixa | null;
  B: number;
  Bb: number;
  E: number;
  S: number;
  P: number;
}

/**
 * Bonificação dos consumidores aceitos num mês. A faixa vem da soma do consumo
 * de quem entra e vale para todos. Por consumidor:
 *   fatura com desconto = consumo × R$ 1,16 × (1 − desconto)
 *   bonificação = fatura × % da faixa (teto: consumo × R$ 1,16) × redutor
 *   Pix I = 30% · Pix II = o resto · recorrente (Variável) = fatura × 2% × redutor
 */
export function calcularBonificacao(lista: ConsumidorBonificacao[]): ResultadoBonificacao {
  const linhas: LinhaBonificacao[] = lista.map(x => {
    const dInt = Number.isFinite(x.d) && Math.abs(x.d - Math.round(x.d)) < 1e-9;
    const sg = segmentoDe(x.c);
    const dOk = dInt && x.d >= R.padrao && x.d <= R.maximo && (!sg || x.d <= sg.max);
    return {
      c: x.c,
      d: x.d,
      dInt,
      dOk,
      sg,
      dSeg: !!(sg && dInt && x.d > sg.max && x.d <= R.maximo),
      ok: Number.isFinite(x.c) && x.c >= R.minimo && dOk,
      doc: x.doc || '',
      F: 0,
      Bb: 0,
      B: 0,
      perda: 0,
      E: 0,
      S: 0,
      P: 0,
      teto: false,
    };
  });
  const vistos: Record<string, true> = {};
  for (const l of linhas) {
    const t = docTipo(l.doc);
    if (!l.ok || !t || t === 'x') continue;
    const k = docLimpo(l.doc);
    if (vistos[k]) {
      l.ok = false;
      l.dup = true;
    }
    vistos[k] = true;
  }
  const V = linhas.reduce((s, l) => s + (l.ok ? l.c : 0), 0);
  const fx = V > 0 ? faixaDe(V) : null;
  let B = 0, Bb = 0, E = 0, S = 0, P = 0;
  for (const l of linhas) {
    if (!l.ok || !fx) continue;
    l.F = cent(l.c * R.tarifa * (1 - l.d / 100));
    const tetoC = cent(l.c * R.tarifa);
    const b = l.F * fx.pct;
    l.teto = b > tetoC;
    l.Bb = cent(l.teto ? tetoC : b);
    l.B = cent(l.Bb * redutor(l.d));
    l.perda = cent(l.Bb - l.B);
    l.E = cent(l.B * R.entrada);
    l.S = cent(l.B - l.E);
    l.P = cent(l.F * recPct(l.d));
    B += l.B;
    Bb += l.Bb;
    E += l.E;
    S += l.S;
    P += l.P;
  }
  return { linhas, V, fx, B: cent(B), Bb: cent(Bb), E: cent(E), S: cent(S), P: cent(P) };
}

/** Fatura com desconto de um consumidor, mesmo fora da conta do mês (0 se não entra). */
export function faturaComDesconto(l: Pick<LinhaBonificacao, 'ok' | 'c' | 'd'>): number {
  return l.ok ? cent(l.c * R.tarifa * (1 - l.d / 100)) : 0;
}

/** Por que um consumidor não entra na conta do mês (texto para o parceiro). */
export function motivoForaDaConta(l: LinhaBonificacao): string {
  if (l.dup) return 'CPF/CNPJ repetido';
  if (!Number.isFinite(l.c)) return 'falta o consumo';
  if (l.c < R.minimo) return `abaixo de ${R.minimo} kWh`;
  if (l.dSeg && l.sg) return `segmento ${l.sg.n} vai até ${l.sg.max}%`;
  return 'desconto fora da regra';
}

// ---------- datas de pagamento ----------

/** Índice de mês (ano × 12 + mês − 1), o mesmo do simulador. */
export const indiceMes = (ano: number, mes: number) => ano * 12 + mes - 1;
export const INDICE_INICIO = indiceMes(R.inicio[0], R.inicio[1]);

/** Pix I: dia 10 do mês seguinte ao aceite. */
export function dataPixI(aceite: Date): Date {
  return new Date(aceite.getFullYear(), aceite.getMonth() + 1, 10);
}

/** Pix II (Fixa) e cada recorrente (Variável): dia 10 do mês seguinte à fatura paga. */
export function dataPagamentoAposFatura(pagamento: Date): Date {
  return new Date(pagamento.getFullYear(), pagamento.getMonth() + 1, 10);
}
